/**
 * The shell's header row: open modules, and the controls that used to sit above it.
 *
 * On a WIDE screen it is the familiar tab strip. Right-click a tab for "Close others /
 * Close all".
 *
 * On a PHONE a row of tabs is hopeless — four tabs and the content has nowhere left to
 * go. So the tabs collapse to the name of the one you are on plus a windows button: tap
 * it and every open screen is listed, with the one you are on marked. Same model as the
 * app switcher on the phone itself.
 *
 * It also carries the sidebar toggle, the install button and the user menu whenever there
 * is no navy bar above it — which is everyone except a super admin, because a student or
 * a teacher belongs to exactly one organisation and never needed to be told which.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTabs } from './TabsContext';
import { useAuth } from '../../context/AuthContext';
import { useExplorerState } from './ExplorerContext';
import UserMenu from './UserMenu';
import { InstallAppButton } from '../pwa/InstallApp';
import { X, TreeStructure, SquaresFour, List } from '@phosphor-icons/react';
import {
  ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger,
} from '../ui/context-menu';

export default function TabStrip({ onToggleSidebar, showChrome = false }) {
  const { tabs, activeKey, activate, close, closeOthers, closeAll } = useTabs();
  const explorer = useExplorerState();
  const { user } = useAuth();
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const switcherRef = useRef(null);

  // Close the switcher when the screen behind it changes, or on Escape.
  useEffect(() => { setSwitcherOpen(false); }, [activeKey]);
  useEffect(() => {
    if (!switcherOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setSwitcherOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [switcherOpen]);

  if (!tabs.length && !showChrome) return null;

  const active = tabs.find((t) => t.key === activeKey) || tabs[0];
  const ActiveIcon = active?.icon;

  const tab = (t) => {
    const Icon = t.icon;
    const isActive = t.key === activeKey;
    return (
      <ContextMenu key={t.key}>
        <ContextMenuTrigger asChild>
          <div
            role="tab"
            aria-selected={isActive}
            tabIndex={0}
            onClick={() => activate(t.key)}
            onKeyDown={(e) => { if (e.key === 'Enter') activate(t.key); }}
            onAuxClick={(e) => { if (e.button === 1) close(t.key); }}
            className={`group flex items-center gap-2 h-9 pl-3 pr-2 rounded-t-md border border-b-0 text-[13px] whitespace-nowrap cursor-pointer select-none transition-colors ${
              isActive
                ? 'bg-white border-border text-shell-primary font-semibold shadow-[inset_0_2px_0_0_hsl(var(--shell-active))] -mb-px'
                : 'bg-muted/40 border-transparent text-foreground/70 hover:bg-muted hover:text-foreground'
            }`}
            data-testid={`tab-${t.key}`}
          >
            {Icon && <Icon className="w-3.5 h-3.5" weight={isActive ? 'fill' : 'regular'} />}
            <span>{t.label}</span>
            {t.pinned ? <span className="w-4" /> : (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); close(t.key); }}
                className="w-5 h-5 rounded flex items-center justify-center text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                title="Close"
                data-testid={`tab-close-${t.key}`}
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        </ContextMenuTrigger>
        <ContextMenuContent>
          {!t.pinned && <ContextMenuItem onClick={() => close(t.key)}>Close</ContextMenuItem>}
          <ContextMenuItem onClick={() => closeOthers(t.key)}>Close others</ContextMenuItem>
          <ContextMenuItem onClick={closeAll}>Close all</ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    );
  };

  return (
    <div className="shell-tabstrip relative flex items-end gap-1 px-2 pt-1.5 bg-muted/60 border-b border-border" data-testid="shell-tabstrip">
      {/* The menu button lives here when there is no bar above us. */}
      {showChrome && (
        <button
          type="button"
          onClick={onToggleSidebar}
          className="lg:hidden h-9 w-9 mb-px rounded-md flex items-center justify-center flex-shrink-0 text-foreground/70 hover:bg-white/60 hover:text-foreground"
          title="Menu"
          data-testid="shell-menu-toggle"
        >
          <List className="w-5 h-5" />
        </button>
      )}

      {explorer.present && (
        <button
          type="button"
          onClick={explorer.toggle}
          className={`h-9 w-8 mb-px mr-1 rounded-t-md flex items-center justify-center flex-shrink-0 transition-colors ${explorer.open ? 'text-shell-primary bg-white border border-b-0 border-border' : 'text-muted-foreground hover:text-foreground hover:bg-white/60'}`}
          title={explorer.open ? 'Hide tree' : 'Show tree'}
          aria-pressed={explorer.open}
          data-testid="explorer-toggle"
        >
          <TreeStructure className="w-4 h-4" weight={explorer.open ? 'fill' : 'regular'} />
        </button>
      )}

      {/* Phone: just where you are. Tabs would leave no room for the page itself. */}
      {active && (
        <div className="lg:hidden flex items-center gap-2 h-9 px-2 min-w-0 flex-1 text-[13px] font-semibold text-shell-primary"
          data-testid="active-tab-label">
          {ActiveIcon && <ActiveIcon className="w-4 h-4 flex-shrink-0" weight="fill" />}
          <span className="truncate">{active.label}</span>
        </div>
      )}

      {/* Wide screens keep the strip. */}
      <div className="hidden lg:flex items-end gap-1 min-w-0 flex-1 overflow-x-auto">
        {tabs.map(tab)}
      </div>

      <div className="flex items-center gap-1 flex-shrink-0 mb-px ml-auto lg:ml-0">
        {/* The app switcher — only worth a button when there is more than one screen. */}
        {tabs.length > 1 && (
          <button
            type="button"
            onClick={() => setSwitcherOpen((v) => !v)}
            className={`lg:hidden relative h-9 w-9 rounded-md flex items-center justify-center transition-colors ${switcherOpen ? 'bg-white text-shell-primary border border-border' : 'text-foreground/70 hover:bg-white/60'}`}
            title="Open screens"
            aria-expanded={switcherOpen}
            data-testid="window-switcher-toggle"
          >
            <SquaresFour className="w-5 h-5" weight={switcherOpen ? 'fill' : 'regular'} />
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-shell-primary text-white text-[10px] leading-4 text-center"
              data-testid="window-count">
              {tabs.length}
            </span>
          </button>
        )}
        {showChrome && (
          <>
            <InstallAppButton className="!text-foreground/70 hover:!bg-white/60" />
            <UserMenu tone="light" size="sm" />
          </>
        )}
      </div>

      {/* ------------------------------------------------- the switcher itself */}
      {switcherOpen && (
        <>
          <button type="button" aria-label="Close" onClick={() => setSwitcherOpen(false)}
            className="lg:hidden fixed inset-0 z-40 bg-black/30" data-testid="window-switcher-backdrop" />
          <div ref={switcherRef}
            className="lg:hidden absolute right-2 top-full z-50 mt-1 w-[min(20rem,calc(100vw-1rem))] rounded-lg border border-border bg-white shadow-xl overflow-hidden"
            data-testid="window-switcher">
            <div className="flex items-center gap-2 px-3 h-9 border-b border-border bg-muted/40">
              <SquaresFour className="w-4 h-4 text-shell-primary" />
              <span className="text-[12px] font-semibold mr-auto">Open screens</span>
              <span className="text-[11px] text-muted-foreground">{tabs.length}</span>
            </div>
            <ul className="max-h-[60vh] overflow-y-auto">
              {tabs.map((t) => {
                const Icon = t.icon;
                const isActive = t.key === activeKey;
                return (
                  <li key={t.key} className={`flex items-center border-b border-border last:border-b-0 ${isActive ? 'bg-shell-primary/5' : ''}`}>
                    <button
                      type="button"
                      onClick={() => { activate(t.key); setSwitcherOpen(false); }}
                      className="flex items-center gap-2 flex-1 min-w-0 px-3 py-2.5 text-left hover:bg-muted/50"
                      data-testid={`switch-to-${t.key}`}
                    >
                      {Icon && <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-shell-primary' : 'text-muted-foreground'}`}
                        weight={isActive ? 'fill' : 'regular'} />}
                      <span className={`text-[13px] truncate ${isActive ? 'font-semibold text-shell-primary' : ''}`}>{t.label}</span>
                      {isActive && <span className="ml-auto text-[10px] uppercase tracking-wide text-muted-foreground flex-shrink-0">on screen</span>}
                    </button>
                    {!t.pinned && (
                      <button
                        type="button"
                        onClick={() => close(t.key)}
                        className="w-9 h-9 flex-shrink-0 flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                        title={`Close ${t.label}`}
                        data-testid={`switch-close-${t.key}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
            {tabs.length > 1 && (
              <button
                type="button"
                onClick={() => { closeOthers(activeKey); setSwitcherOpen(false); }}
                className="w-full px-3 py-2 text-[12px] text-muted-foreground hover:bg-muted/50 border-t border-border"
                data-testid="switcher-close-others"
              >
                Close the others
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
